import { Box, createStyles, Divider, IconButton, useTheme } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { CreateCSSProperties, makeStyles } from '@material-ui/styles';
import React, { PropsWithChildren, ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { Label, Space } from '@/components';
import { BaseContainer, HeaderBox } from '@/layout/components';
import { BaseContainerProps } from '@/layout/components/BaseContainer';
import { makeSelectIsMobileView } from '@/model/global/selectors';
import { Func } from '@/utils/ts';

const useStyles = makeStyles((theme: Theme) => {
  const baseContainer: CreateCSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    flexGrow: 1,
  };
  const mainBox = baseContainer;
  return createStyles({
    container: {
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      color: theme.colors.primaryBackground,
      right: 0,
      marginTop: '-50px',
    },
    mainBox: {
      ...mainBox,
      backgroundColor: theme.colors.primaryBackground,
      width: theme.MainContainer.width,
    },
    mainBoxMobile: {
      ...mainBox,
    },
    labelMobile: {
      ...mainBox,
      flexGrow: 0,
      alignItems: 'center',
      flexShrink: 1,
      overflow: 'visible',
      paddingTop: '14px',
      paddingBottom: '12px',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  });
});

export interface ModalLayoutProps extends PropsWithChildren<Omit<BaseContainerProps, 'children'>> {
  'data-test'?: string;
  title?: ReactNode;
  onClose: Func;
}

const selectIsMobile = makeSelectIsMobileView();

export const ModalLayout: React.FC<ModalLayoutProps> = ({
  title,
  height,
  children,
  onClose,
  'data-test': dataTest,
  ...props
}) => {
  const classes = useStyles();
  const isMobile = useSelector(selectIsMobile);
  const baseClasses = useTheme();
  return isMobile ? (
    <Space direction="vertical" size={0} style={{ overflow: 'auto', flexGrow: 1 }}>
      <Space direction="vertical" size={0} className={classes.labelMobile}>
        <Label data-test={dataTest && `${dataTest}-title`}>{title}</Label>
        <Divider orientation="horizontal" />
      </Space>
      <Box className={classes.mainBoxMobile}>{children}</Box>
    </Space>
  ) : (
    <BaseContainer
      containerClassname={classes.container}
      containerPrefix={
        <IconButton onClick={onClose} className={classes.closeButton} data-test={dataTest && `${dataTest}-close`}>
          <CloseIcon />
        </IconButton>
      }
      height={(height && height !== 'unset' ? height : baseClasses.MainContainer.height) + 50}
      width={baseClasses.MainContainer.width + 100}
      {...props}
    >
      <HeaderBox data-test={dataTest && `${dataTest}-header`} title={title} />
      <Box className={classes.mainBox}>{children}</Box>
    </BaseContainer>
  );
};

export default ModalLayout;
